import React, {
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  KeyboardEvent,
  useRef,
  useState,
} from 'react';
import { SingleInput, Wrapper } from './PinCodeInput.styles';

export const BACKSPACE_KEY = 'Backspace';

export const LEFT_ARROW_KEY = 'ArrowLeft';

export const RIGHT_ARROW_KEY = 'ArrowRight';

const E_KEY = 'Enter';

export type InputMode = 'none' | 'text' | 'decimal' | 'numeric' | 'tel';

export type PseudoEventTarget = {
  name: string;
  value: string;
};

export type PseudoInputEvent = {
  target: PseudoEventTarget;
};

export type PinCodeInputProps = {
  id?: string;
  type?: string;
  inputLength: number;
  name: string;
  onChange?: (e: PseudoInputEvent) => void;
  disabled?: boolean;
  forceUppercase?: boolean;
  filterChars?: string[];
  defaultValue?: string;
  autoComplete?: 'one-time-code' | 'off';
  inputFilterFn?: (char: string) => boolean;
  style?: CSSProperties;
  inputMode?: InputMode;
  ariaLabel?: string;
};

function focusAndSelect(target: HTMLInputElement) {
  target.focus();
  target.select();
}

export const PinCodeInput = ({
  inputLength = 4,
  forceUppercase,
  defaultValue = '',
  onChange,
  style = {},
  id,
  type = 'text',
  autoComplete = 'off',
  name,
  ariaLabel,
  ...rest
}: PinCodeInputProps) => {
  const textInput = useRef(
    Array(inputLength)
      .fill(0)
      .map(() => React.createRef<HTMLInputElement>())
  );

  const initialValue = forceUppercase ? defaultValue.toUpperCase() : defaultValue;
  const [input, setInput] = useState<string[]>(
    Array(inputLength)
      .fill(0)
      .map((_, i) => initialValue[i] || '')
  );

  const reportChange = (value: string) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value,
        },
      });
    }
  };

  const getNewInput = (value: string, pinIndex: number, previousValue: string[]) => {
    const isMultiCharacterChange = value.length > 1; // Is true if the user pastes in a code

    if (isMultiCharacterChange) {
      return previousValue.map((pin, i) => {
        const isInRange = i - pinIndex < value.length;

        if (i >= pinIndex && isInRange) {
          return value[i - pinIndex];
        }

        return pin;
      });
    }

    return previousValue.map((pin, i) => (i === pinIndex ? value : pin));
  };

  const getNextFocusTarget = (value: string, pinIndex: number) => {
    const maxIndex = inputLength - 1;

    const isNextIndexInRange = pinIndex + value.length >= maxIndex;
    const nextIndex = isNextIndexInRange ? maxIndex : pinIndex + value.length;
    const newTarget = textInput.current[nextIndex];

    return newTarget;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const value = String(target.value);
    const previousInput = input;

    if (value !== '') {
      const pinIndex = Number(target.dataset.id) || 0;
      const newInput = getNewInput(value, pinIndex, previousInput);
      const newTarget = getNextFocusTarget(value, pinIndex);

      if (newTarget && newTarget.current) {
        focusAndSelect(newTarget.current);
      }

      const fullValue = newInput.join('');
      reportChange(fullValue);

      setInput(newInput);
    } else {
      reportChange(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { currentTarget } = e;
    const targetIndex = Number(currentTarget.dataset.id);
    const nextTarget = textInput.current[targetIndex + 1];
    const prevTarget = textInput.current[targetIndex - 1];

    if (e.metaKey && e.key === 'v') {
      // Pasting, will trigger an onChange
      return;
    }

    if (e.shiftKey && e.key === LEFT_ARROW_KEY) {
      // User is selecting
      return;
    }

    let newInput;
    let value;

    switch (e.key) {
      case BACKSPACE_KEY:
        e.preventDefault();
        newInput = input.slice();
        newInput[targetIndex] = '';
        value = newInput.join('');

        setInput(newInput);

        if (prevTarget && prevTarget.current) {
          focusAndSelect(prevTarget.current);
        }
        reportChange(value);
        break;

      case LEFT_ARROW_KEY:
        e.preventDefault();

        if (prevTarget && prevTarget.current) {
          focusAndSelect(prevTarget.current);
        }
        break;

      case RIGHT_ARROW_KEY:
        e.preventDefault();

        if (nextTarget && nextTarget.current) {
          focusAndSelect(nextTarget.current);
        }
        break;

      case E_KEY: // This case needs to be handled because of https://stackoverflow.com/questions/31706611/why-does-the-html-input-with-type-number-allow-the-letter-e-to-be-entered-in
        if (type === 'number') {
          e.preventDefault();
        }
        break;

      default:
        break;
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <Wrapper style={style}>
      {input.map((v: string, i: number) => {
        return (
          <SingleInput
            ref={textInput.current[i]}
            aria-label={ariaLabel}
            data-testid="pincode-input"
            data-id={i}
            value={v}
            /* eslint-disable-next-line react/no-array-index-key */
            key={`input_${i}`}
            min={0}
            max={9}
            maxLength={inputLength}
            autoComplete={autoComplete}
            onFocus={handleFocus}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            {...rest}
            type={type}
          />
        );
      })}
    </Wrapper>
  );
};
